import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Settings, LogIn, ChevronDown, Users as UsersIcon } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";

/**
 * Top-right user menu. Shows login button when logged out, otherwise an
 * avatar bubble + dropdown with role-aware actions.
 */
export default function UserMenu({ variant = "light" }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const dark = variant === "dark";

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (user === undefined) {
    return <div className="h-8 w-16 animate-pulse rounded-sm bg-slate-200/40" />;
  }

  if (!user) {
    return (
      <Link to="/login"
            className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.16em] transition ${
              dark ? "border-white/15 text-white hover:bg-white/10" : "border-slate-300 text-ink hover:bg-slate-50"
            }`}
            data-testid="topnav-login-btn">
        <LogIn className="h-3.5 w-3.5" /> Ingresar
      </Link>
    );
  }

  const initials = (user.name || user.email || "U")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-sm border px-2 py-1 text-[11px] transition ${
                dark ? "border-white/15 text-white hover:bg-white/10" : "border-slate-300 text-ink hover:bg-slate-50"
              }`}
              data-testid="topnav-user-btn">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-gradient-to-br from-accent-blue to-accent-purple font-mono text-[9px] font-bold text-white">
          {initials}
        </span>
        <span className="hidden font-medium sm:inline">{user.name || user.email.split("@")[0]}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-64 rounded-sm border border-slate-200 bg-white p-1 shadow-2xl"
             data-testid="topnav-user-menu">
          <div className="border-b border-slate-100 p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{user.role}</p>
            <p className="font-display text-sm text-ink">{user.name || "Sin nombre"}</p>
            <p className="text-[11px] text-slate-500">{user.email}</p>
          </div>
          {user.role === "admin" && (
            <Link to="/admin/usuarios" onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-sm px-3 py-2 text-[12px] text-ink hover:bg-slate-50"
                  data-testid="user-menu-users">
              <UsersIcon className="h-3.5 w-3.5 text-accent-blue" /> Gestión de usuarios
            </Link>
          )}
          <Link to="/dashboard" onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-sm px-3 py-2 text-[12px] text-ink hover:bg-slate-50">
            <Settings className="h-3.5 w-3.5 text-slate-500" /> Panel
          </Link>
          <button onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-[12px] text-red-700 hover:bg-red-50"
                  data-testid="user-menu-logout">
            <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
